export function formatPrice(price: number, decimals: number): string {
  return price.toFixed(decimals);
}

/**
 * Display decimals for a price when the venue's tick size is unknown:
 * 2 above 100, more for small prices so sub-cent coins stay readable.
 */
export function priceDecimalsFor(price: number): number {
  const p = Math.abs(price);
  if (!Number.isFinite(p) || p === 0) return 2;
  if (p >= 100) return 2;
  if (p >= 1) return 4;
  if (p >= 0.01) return 5;
  if (p >= 0.0001) return 7;
  return Math.min(12, Math.ceil(-Math.log10(p)) + 3);
}

// toLocaleString with options builds a new Intl.NumberFormat on every call, which showed up in page-load
// profiles (the legend formats on every tick); one formatter per decimal count is built once instead.
const grouped = new Map<number, Intl.NumberFormat>();

/** Thousands-separated price with fixed decimals, e.g. 61,234.50. */
export function formatPriceGrouped(price: number, decimals: number): string {
  if (!Number.isFinite(price)) return '—';
  let format = grouped.get(decimals);
  if (!format) {
    format = new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    grouped.set(decimals, format);
  }
  return format.format(price);
}

/** Compact number for indicator values: 1.23M, 45.6K, 0.0123. */
export function formatCompact(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (abs >= 1e4) return `${(value / 1e3).toFixed(2)}K`;
  if (abs >= 100) return value.toFixed(2);
  if (abs >= 1) return value.toFixed(3);
  if (abs === 0) return '0';
  return value.toPrecision(4);
}

export function formatVolume(volume: number): string {
  if (volume >= 1_000_000_000) return `${(volume / 1_000_000_000).toFixed(2)}B`;
  if (volume >= 1_000_000) return `${(volume / 1_000_000).toFixed(2)}M`;
  if (volume >= 1_000) return `${(volume / 1_000).toFixed(2)}K`;
  return volume.toFixed(2);
}

export function formatPercentage(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

import type { Timeframe } from '../types/ohlcv';
import { ApiError } from '../api/client';

export function formatTimestamp(ts: number, timeframe: Timeframe): string {
  const date = new Date(ts);
  switch (timeframe) {
    case '1m':
    case '5m':
    case '15m':
      return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    case '1h':
    case '4h':
      return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    case '1D':
    case '1W':
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    default:
      return date.toLocaleString();
  }
}

export function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    const body = error.body as { message?: unknown } | null;
    if (body && typeof body.message === 'string') return body.message;
    return error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
